<template>
    <csp-desktop-info-card
        v-if="formContext.isLoaded"
        :cols="12"
        :form-context="formContext"
        icon="inticon-users"
        title="Gutachter"
        @save="saveAppraiserFormData">
        <b-row>
            <csp-desktop-info-card-element
                class="col-6"
                :form-context="formContext"
                label="Gutachterfirma"
                :options-loader="loadAppraiserFirmOptions"
                property="appraiserFirm"
                type="autocomplete">
                <template v-slot:display-value>
                    <p>
                        <span v-if="firmData.name"><b>{{ firmData.name }}</b><br></span>
                        <span v-if="firmData.street">{{ firmData.street }}<br></span>
                        <span v-if="firmData.postalCode || firmData.city">
                            {{ firmData.postalCode }} {{ firmData.city }}<br>
                        </span>
                        <span v-if="firmData.countryName">{{ firmData.countryName }}<br></span>
                    </p>
                    <p>
                        <span v-if="firmData.phone">{{ firmData.phone }}<br></span>
                        <span v-if="firmData.email">
                            <a :href="'mailto:' + firmData.email">{{ firmData.email }}</a>
                        </span>
                    </p>
                </template>
            </csp-desktop-info-card-element>
            <csp-desktop-info-card-element
                class="col-6"
                :dependencies="['appraiserFirm']"
                :form-context="formContext"
                label="Gutachterbenutzer"
                :options-loader="loadAppraiserUserOptions"
                property="appraiserUser"
                type="autocomplete">
                <template v-slot:display-value>
                    <p>
                        <span v-if="userData.name"><b>{{ userData.name }}</b><br></span>
                        <span v-if="userData.street">{{ userData.street }}<br></span>
                        <span v-if="userData.postalCode || userData.place">
                            {{ userData.postalCode }} {{ userData.place }}<br>
                        </span>
                        <span v-if="userData.countryName">{{ userData.countryName }}<br></span>
                    </p>
                    <p>
                        <span v-if="userData.phone">{{ userData.phone }}<br></span>
                        <span v-if="userData.email">
                            <a :href="'mailto:' + userData.email">{{ userData.email }}</a>
                        </span>
                    </p>
                </template>
            </csp-desktop-info-card-element>
        </b-row>
        <div v-if="formContext.permissions.canSendOrderToAppraiser" class="float-right">
            <b-button variant="primary" @click="loadMailAndOpenMailModal">
                <i class="inticon-autoclaim_mail"/> Gutachter-Link
            </b-button>
            <b-modal v-model="isMailModalOpen"
                :cancel-disabled="true"
                :hide-footer="true"
                :ok-disabled="true"
                size="lg"
                title="Gutachter-Link versenden">
                <div v-if="isMailModalLoading" class="loading-content-container d-flex justify-content-center">
                    <csp-loading-indicator/>
                </div>
                <template v-if="appraiserMailDocument && appraiserMailDocumentPath" v-slot:default="{ visible }">
                    <div v-if="visible" class="print-documents-modal-content">
                        <csp-record-detail-document-mail
                            ref="documentMail"
                            :mail-document="appraiserMailDocument"
                            :path="appraiserMailDocumentPath"
                            @cancel-mail="cancelMail"
                            @close-mail-modal="isMailModalOpen = false"
                            @mail-sent="$emit('document-generated')"/>
                    </div>
                </template>
            </b-modal>
        </div>
        <div class="clear-float"/>
    </csp-desktop-info-card>
</template>

<script>
    import Container from '@services/Container';
    import FormDataMixin from '../../mixins/FormDataMixin';
    import DocumentMailModal from '@desktop/components/record/document/DocumentMailModal.vue';


    const container = Container.getInstance();

    const recordRepository = container.getDesktopRecordRepository();
    const ajaxService = container.getAjaxService();

    export default {
        name: 'ParticipantAppraiser',
        components: {
            'csp-record-detail-document-mail': DocumentMailModal,
        },
        mixins: [FormDataMixin],
        emits: ['document-generated'],
        data: function() {
            return {
                isMailModalOpen: false,
                isMailModalLoading: true,
                appraiserMailDocument: null,
                appraiserMailDocumentPath: null,
            };
        },
        computed: {
            recordId() {
                return this.$route.params.recordId;
            },
            userData() {
                return {
                    'name': this.formContext.form.getProperty('userName') ?? '',
                    'street': this.formContext.form.getProperty('userStreet') ?? '',
                    'postalCode': this.formContext.form.getProperty('userPostalCode') ?? '',
                    'place': this.formContext.form.getProperty('userPlace') ?? '',
                    'countryName': this.formContext.form.getProperty('userCountryName') ?? '',
                    'phone': this.formContext.form.getProperty('userPhone') ?? '',
                    'email': this.formContext.form.getProperty('userEmail') ?? '',
                };
            },
            firmData() {
                return {
                    'name': this.formContext.form.getProperty('firmName') ?? '',
                    'street': this.formContext.form.getProperty('firmStreet') ?? '',
                    'postalCode': this.formContext.form.getProperty('firmPostalCode') ?? '',
                    'city': this.formContext.form.getProperty('firmCity') ?? '',
                    'countryName': this.formContext.form.getProperty('firmCountryName') ?? '',
                    'phone': this.formContext.form.getProperty('firmPhone') ?? '',
                    'email': this.formContext.form.getProperty('firmEmail') ?? '',
                };
            },
        },
        methods: {
            getDocumentCreationPath(document, format) {
                // TODO Move to recordRepository and declare type for document
                const urlParameters = document.urlOptions;

                let urlPath = '/de';
                urlPath += '/' + urlParameters.module;
                urlPath += '/' + urlParameters.controller;
                urlPath += '/' + urlParameters.action;
                urlPath += '/sessionKey/' + urlParameters.sessionKey;
                urlPath += '/format/' + format;

                return ajaxService.addSessionIdQueryParameter(urlPath);
            },
            loadAppraiserFirmOptions() {
                return recordRepository.loadAppraiserFirmOptions(this.recordId);
            },
            loadAppraiserUserOptions() {
                const appraiserFirmId = this.formContext.form.getProperty('appraiserFirm');
                return recordRepository.loadAppraiserUserOptions(this.recordId, appraiserFirmId);
            },
            loadMailAndOpenMailModal() {
                this.isMailModalOpen = true;
                recordRepository.loadAppraiserSendOrderMail(this.recordId).then((response) => {
                    response.data.forEach((data) => {
                        const attributes = data.attributes;
                        const type = attributes.type;

                        if (type !== 'mail') {
                            return;
                        }

                        if (data.id === 'OrderToAppraiserByDealer' || data.id === 'AssessorNotification') {
                            this.appraiserMailDocument = {
                                'documentId': data.id,
                                'key': data.id,
                                ...data.attributes,
                            };

                            this.appraiserMailDocumentPath = this.getDocumentCreationPath(
                                this.appraiserMailDocument,
                                type
                            );
                        }
                    });
                }).finally(() => {
                    this.isMailModalLoading = false;
                });
            },
            cancelMail() {
                this.isMailModalOpen = false;
                this.isMailModalLoading = true;
            },
            saveAppraiserFormData(formContext) {
                const saveFunction = (objectId, postData) => {
                    return recordRepository.saveParticipantsAppraiser(this.recordId, postData)
                }
                this.saveFormData(formContext, saveFunction, null);
            },
        },
    }
</script>
